import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, LucideIcon } from 'lucide-react';
import PageGlow from '@/components/PageGlow';
import HeroWave from '@/components/HeroWave';
import CaseStudyCard from '@/components/CaseStudyCard';
import ServiceFinalBanner from '@/components/ServiceFinalBanner';
import Eyebrow from '@/components/Eyebrow';

const GRAD    = '#5048E5';
const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border  = 'hsl(var(--border))';

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};
const stagger = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.09 } },
};

const FONT = 'Plus Jakarta Sans, sans-serif';
const HEAD = 'Outfit, sans-serif';

export interface ServiceStoryProps {
  tagline: string;
  Icon: LucideIcon;
  /** Accent colour, e.g. 'hsl(243 75% 59%)'. */
  color: string;
  /** The single primary call-to-action label, reused everywhere on the page. */
  ctaText: string;

  /** Hero — a plain-English outcome, not the service name repeated. */
  heroHeadline: string;
  heroSub: string;

  /** "The problem" — spoken in the small-business owner's own words. */
  problem: { heading: string; body: string[]; footer?: ReactNode };

  /** "What you get" — concrete deliverables. */
  whatYouGetIntro?: string;
  whatYouGet: { heading: string; body: string }[];

  /** "How it works" — 3–4 simple steps. */
  howItWorks: { title: string; body: string }[];

  /** One proof element — the reused Eclipse Paving case study card. */
  showCaseStudy?: boolean;
  proofHeading?: string;
  proofSub?: string;

  /** Pricing lives at the bottom, value-first. */
  pricing: { price: string; caption?: string };

  /** Closing call-to-action. */
  finalHeading: string;
  finalSub: string;

  /** Optional FAQ block rendered before the final CTA. */
  faqs?: { q: string; a: string }[];
}



const CtaButton = ({ label }: { label: string }) => (
  <Link to="/get-started"
    className="btn-glow inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all"
    style={{ background: GRAD, color: '#ffffff', fontFamily: FONT }}>
    {label} <ArrowRight size={15} />
  </Link>
);

export default function ServiceStoryPage({
  tagline, Icon, color, ctaText,
  heroHeadline, heroSub,
  problem, whatYouGetIntro, whatYouGet, howItWorks,
  showCaseStudy = true, proofHeading = 'Real results, real business', proofSub,
  pricing, finalHeading, finalSub, faqs,
}: ServiceStoryProps) {
  return (
    <>
      {/* ── Hero — the outcome for the customer ── */}
      <section className="relative overflow-hidden py-6 md:py-10" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave />
        <PageGlow color={color} tall />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp}>
              <Eyebrow color={color} Icon={Icon}>{tagline}</Eyebrow>
            </motion.div>
            <motion.h1 variants={fadeUp} className="mb-4 text-4xl font-bold md:text-5xl"
              style={{ fontFamily: HEAD, color: fg, lineHeight: 1.06 }}>
              {heroHeadline}
            </motion.h1>
            <motion.p variants={fadeUp} className="mb-6 text-lg leading-relaxed"
              style={{ fontFamily: FONT, color: fgMuted }}>
              {heroSub}
            </motion.p>
            <motion.div variants={fadeUp} className="flex items-center justify-center">
              <CtaButton label={ctaText} />
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* ── The problem — in the owner's own words ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.35 }}
          className="mx-auto max-w-3xl px-6 text-center">
          <Eyebrow color={color}>The problem</Eyebrow>
          <h2 className="mb-6 text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg, lineHeight: 1.12 }}>
            {problem.heading}
          </h2>
          {problem.body.map((p, i) => (
            <p key={i} className="mx-auto mb-4 max-w-2xl text-base leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
              {p}
            </p>
          ))}
          {problem.footer && (
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
              {problem.footer}
            </p>
          )}
        </motion.div>
      </section>

      {/* ── What you get — concrete deliverables ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <motion.div>
            <motion.div className="mb-10 text-center">
              <h2 className="mb-3 text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
                What you get
              </h2>
              {whatYouGetIntro && (
                <p className="mx-auto max-w-2xl text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
                  {whatYouGetIntro}
                </p>
              )}
            </motion.div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {whatYouGet.map(({ heading, body }) => (
                <motion.div key={heading} className="rounded-2xl p-6" style={{
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid var(--glass-border)',
                    boxShadow: 'var(--glass-shadow)',
                  }}>
                  <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${color}18` }}>
                    <CheckCircle2 size={16} style={{ color }} />
                  </div>
                  <h3 className="mb-1.5 font-bold" style={{ fontFamily: HEAD, color: fg }}>{heading}</h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>{body}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── How it works — a few simple steps ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <motion.h2 className="mb-10 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
            How it works
          </motion.h2>
          <motion.div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {howItWorks.map(({ title, body }, i) => (
              <motion.div key={title} className="flex flex-col rounded-2xl p-6" style={{ background: surface, border: `1px solid ${border}` }}>
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
                  style={{ background: `${color}18`, color, fontFamily: HEAD }}>
                  {i + 1}
                </div>
                <h3 className="mb-1.5 font-bold" style={{ fontFamily: HEAD, color: fg }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>{body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Proof — the reused Eclipse Paving case study ── */}
      {showCaseStudy && (
        <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
          <div className="mx-auto flex max-w-5xl flex-col items-center px-6 text-center">
            <motion.div className="mb-8">
              <h2 className="mb-3 text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
                {proofHeading}
              </h2>
              {proofSub && (
                <p className="mx-auto max-w-2xl text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
                  {proofSub}
                </p>
              )}
            </motion.div>
            <motion.div className="flex justify-center">
              <CaseStudyCard />
            </motion.div>
          </div>
        </section>
      )}

      {/* ── Pricing — at the bottom, value-first ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <h2 className="mb-3 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
            Simple, transparent pricing
          </h2>
          <p className="mx-auto mb-8 max-w-xl text-center text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
            No surprises — you&apos;ll always get a fixed quote and a clear service agreement before any work begins.
          </p>
          <motion.div className="mx-auto flex max-w-md flex-col items-center rounded-2xl p-8 text-center" style={{ background: surface, border: `1px solid ${color}45` }}>
            <p className="mb-2 text-3xl font-bold" style={{ fontFamily: HEAD, color: fg }}>
              {pricing.price}
            </p>
            {pricing.caption && (
              <p className="text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>
                {pricing.caption}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* ── FAQ — optional, honest plain answers ── */}
      {faqs && faqs.length > 0 && (
        <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
          <div className="mx-auto max-w-3xl px-6">
            <h2 className="mb-8 text-center text-2xl font-bold md:text-3xl" style={{ fontFamily: HEAD, color: fg }}>
              Common questions
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="group rounded-2xl p-5" style={{ background: surface, border: `1px solid ${border}` }}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-bold" style={{ fontFamily: HEAD, color: fg }}>
                    <span>{q}</span>
                    <ArrowRight size={16} className="shrink-0 transition-transform group-open:rotate-90" style={{ color }} />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed" style={{ fontFamily: FONT, color: fgMuted }}>{a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Final CTA — one clear next step, shared across all service pages ── */}
      <ServiceFinalBanner heading={finalHeading} sub={finalSub} ctaText={ctaText} />
    </>
  );
}
